import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { AuthService } from 'src/app/shared/services/auth.service';
import { UsersService } from 'src/app/shared/services/users.service';

@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss']
})
export class ChangePasswordComponent {
  passwordForm: FormGroup;
  hideCurrent = true;
  hideNew = true;
  hideConfirm = true;
  loading = false;

  constructor(
    public dialogRef: MatDialogRef<ChangePasswordComponent>,
    private apiService: AuthService,
    private snackBar: MatSnackBar,
    private router: Router,
    private formBuilder: FormBuilder,
    private dialog: MatDialog,
    private _UsersService: UsersService) {

    this.passwordForm = this.formBuilder.group({
      currentPassword: ['', Validators.required],
      newPassword: ['', [
        Validators.required,
        Validators.minLength(8),
        Validators.pattern('(?=.*\\d)(?=.*[a-z])(?=.*[A-Z]).{8,}')
      ]],
      confirmPassword: ['', [Validators.required]],
    }, { validator: this.passwordMatchValidator });
  }

  // Password validator
  passwordMatchValidator(form: FormGroup) {
    const newPassword = form.get('newPassword')?.value;
    const confirmPassword = form.get('confirmPassword')?.value;

    if (newPassword !== confirmPassword) {
      form.get('confirmPassword')?.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    } else {
      form.get('confirmPassword')?.setErrors(null);
      return null;
    }
  }

  toggleHideCurrent() {
    this.hideCurrent = !this.hideCurrent;
  }

  toggleHideNew() {
    this.hideNew = !this.hideNew;
  }

  toggleHideConfirm() {
    this.hideConfirm = !this.hideConfirm;
  }

  // Submit Method
  submitForm() {
    if (this.passwordForm.invalid) {
      this.passwordForm.markAllAsTouched();
      this.snackBar.open('Please correct the errors in the form', 'Dismiss', { duration: 3000 });
      return;
    }

    this.apiService.getCurrentUser().subscribe({
      next: user => {
        if (user && user.data.status === 1) {
          if (user.data.role.status === 1) {
            this.changePassword();
          } else {
            this.handleInactiveRole();
          }
        } else {
          this.handleInactiveStatus();
        }
      },
      error: err => {
        this.snackBar.open('Failed to verify user status', 'Dismiss', { duration: 3000 });
      }
    });
  }

  // Change password Method
  changePassword() {
    const formData = new FormData();
    formData.append('old_password', this.passwordForm.value.currentPassword);
    formData.append('new_password', this.passwordForm.value.newPassword);
    formData.append('new_password_confirmation', this.passwordForm.value.confirmPassword);

    this.loading = true;
    this._UsersService.ChangePassword(formData).subscribe({
      next: () => {
        this.snackBar.open('Password Updated Successfully', 'Dismiss', { duration: 3000 });
        this.loading = false;
        this.dialog.closeAll();
        localStorage.clear();
        this.router.navigate(['/admin/login']);
      },
      error: err => {
        this.snackBar.open(err, 'Dismiss', { duration: 3000 });
        this.loading = false;
      }
    });
  }

  // Inactive Role
  handleInactiveRole() {
    this.dialog.closeAll();
    this.snackBar.open('Your role is Inactive', 'Dismiss', { duration: 3000 });
    this.router.navigate(['/admin/login']);
  }

  // Inactive User
  handleInactiveStatus() {
    this.dialog.closeAll();
    this.snackBar.open('Your status is Inactive', 'Dismiss', { duration: 3000 });
    this.router.navigate(['/admin/login']);
  }

  // Error Handing
  getErrorMessage(controlName: string): string {
    const control = this.passwordForm.get(controlName);
    if (control?.hasError('minlength')) {
      return 'Password must be at least 8 characters long';
    }
    if (control?.hasError('pattern')) {
      return 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
    }
    return '';
  }
}
